import './bottom-tab-bar.scss';

import { notification } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';

import {
  clientApi,
  getLeasingCompanyFromParams
} from '@/components/Client/api';
import { redirectToMiniApp } from '@/components/Client/shared/webview/base-actions';
import {
  CLIENT_ROUTES,
  getFullClientRoute
} from '@/components/Shared/routes/routes';
import useAppNavigate from '@/hooks/useAppNavigation';
import CarIcon from '@/media/icons/car.svg?react';
import FilesIcon from '@/media/icons/files.svg?react';
import LeasingOverviewIcon from '@/media/icons/leasing-overview.svg?react';
import LeasingRequestsIcon from '@/media/icons/leasing-requests.svg?react';
import MobileBurgerMenuIcon from '@/media/icons/mobile-burger-menu.svg?react';
import { useAppDispatch, useAppSelector } from '@/store';
import { startGlobalLoading, stopGlobalLoading } from '@/store/loading.slice';

const BottomTabBar: React.FC = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { navigate } = useAppNavigate();
  const { isInWebView } = useAppSelector((state) => state.client);
  const { autoCreditRequest } = useAppSelector(
    (state) => state.clientAutoCredit
  );

  if (!isInWebView) return null;

  const isLeasingCompany =
    !!getLeasingCompanyFromParams() || autoCreditRequest?.sendTo === 'leasing';

  const createPath = getFullClientRoute(CLIENT_ROUTES.autoCreditCreate);
  const requestsPath = getFullClientRoute(CLIENT_ROUTES.autoCredit);

  const isCreateActive = location.pathname.includes(createPath);
  const isRequestsActive =
    location.pathname.includes(requestsPath) && !isCreateActive;

  const getItemClassName = (isActive: boolean): string =>
    `client-tabbar__item ${isActive ? 'client-tabbar__item--active' : ''}`;

  if (isLeasingCompany) {
    const goToLeasingRoute = (route: string) => () => {
      redirectToMiniApp('leasing', {
        initialPath: route
      });
    };

    return (
      <nav className="client-tabbar">
        <div className="client-tabbar__wrapper">
          <button
            className="client-tabbar__item"
            onClick={goToLeasingRoute('/leasing')}
          >
            <LeasingOverviewIcon className="client-tabbar__icon" />
            <span className="client-tabbar__label">
              {t('client.leasing-overview')}
            </span>
          </button>
          <button
            className="client-tabbar__item"
            onClick={goToLeasingRoute('/leasing/requests')}
          >
            <LeasingRequestsIcon className="client-tabbar__icon" />
            <span className="client-tabbar__label">
              {t('client.my-requests')}
            </span>
          </button>
          <button
            className="client-tabbar__item"
            onClick={goToLeasingRoute('/native-menu')}
          >
            <MobileBurgerMenuIcon className="client-tabbar__icon" />
            <span className="client-tabbar__label">
              {t('client.mobile-burger-menu')}
            </span>
          </button>
        </div>
      </nav>
    );
  }

  const handleRequestsClick = async (
    event: React.MouseEvent<HTMLAnchorElement>
  ) => {
    if (isInWebView && !clientApi.isSsoAuthorized()) {
      event.preventDefault();
      dispatch(startGlobalLoading());
      const isSsoAuthorized = await clientApi.ssoAuthorize();
      dispatch(stopGlobalLoading());
      if (!isSsoAuthorized) {
        notification.error({
          message: t('client.sso-authorization-error')
        });
        return;
      }
    }
    navigate(requestsPath + '?tab=active');
  };

  return (
    <nav className="client-tabbar">
      <div className="client-tabbar__wrapper">
        <Link
          to={`/${i18n.language}${createPath}`}
          className={getItemClassName(isCreateActive)}
        >
          <CarIcon className="client-tabbar__icon" />
          <span className="client-tabbar__label">
            {t('client.create-request')}
          </span>
        </Link>

        <Link
          to={`/${i18n.language}${requestsPath}?tab=active`}
          className={getItemClassName(isRequestsActive)}
          onClick={handleRequestsClick}
        >
          <FilesIcon className="client-tabbar__icon" />
          <span className="client-tabbar__label">
            {t('client.my-requests')}
          </span>
        </Link>
      </div>
    </nav>
  );
};

export default BottomTabBar;
